import { useCallback, useEffect, useRef, useState } from 'react'

import { getUploadFailureParams, sendEvent } from '@core/analytics'
import { Logger } from '@core/logger'
import { useActions, useValues } from '@core/store'

import {
    uploadStore,
    type TUploadedAudio,
    type TUploadItem,
    type TUploadItemStatus,
} from '../store/uploadStore'

import { DEFAULT_UPLOAD_CATEGORY_ID } from '../constants/upload'
import { consumePendingHandover } from '../helpers/pendingHandover'
import { generateId } from '../helpers/generateId'
import { validateAudioFile, type TAudioFileError } from '../helpers/validateAudioFile'
import { loadUploadAudioFile } from '../services/uploadAudioFileAsync'

/** Reason codes, not copy: the provider owns i18n. */
export type TUploadFlowError =
    | TAudioFileError
    | 'upload-failed'
    | 'unsupported-file'
    | 'upload-in-progress'
    | 'handover-failed'

export type TUploadSettings = {
    categoryId: number
    downloadable: boolean
    transcribe: boolean
}

export type TQueuedFile = {
    file: File
    fileId: string
}

type TBatchOrigin = {
    batchId: string
    isAdopted: boolean
}

// Re-exported so the provider, the status-label hook and the feature barrel keep importing the
// upload types from the flow they belong to, not from the chunk that happens to hold them.
export type { TUploadedAudio, TUploadItem, TUploadItemStatus }

type TUseAudioUploadFlowParams = {
    onError: (reason: TUploadFlowError, fileName: string) => void
    onBatchStarted: (fileIds: string[]) => void
    onBatchFilesStarted: (queued: TQueuedFile[], origin: { isAdopted: boolean }) => void
    /** The count comes along so the caller can tell "one upload, now review it" from "nothing left". */
    onBatchFinished: (uploaded: TUploadedAudio[], queuedCount: number) => void
}

const DEFAULT_SETTINGS: TUploadSettings = {
    categoryId: DEFAULT_UPLOAD_CATEGORY_ID,
    downloadable: true,
    transcribe: false,
}

/**
 * The success ping after each transfer is what triggers transcoding — without it the file silently
 * never appears. Uploads run one at a time (audio-web allows three); the provider that mounts this
 * hook sits above the router, so an in-flight upload deliberately is NOT aborted on unmount.
 */
export const useAudioUploadFlow = ({
    onError,
    onBatchStarted,
    onBatchFilesStarted,
    onBatchFinished,
}: TUseAudioUploadFlowParams) => {
    // The picked `File`s never reach the store: they cannot be serialized, and a reload has to lose
    // them anyway. Only what survives a reload — the batch and its progress — lives in the chunk.
    const [pendingFiles, setPendingFiles] = useState<File[]>([])
    const [storeItems, isUploading, restorableItems, displayedFileIds, handover] = useValues([
        uploadStore.props.getItems,
        uploadStore.props.getIsUploading,
        uploadStore.props.getRestorableItems,
        uploadStore.props.getDisplayedFileIds,
        uploadStore.props.getHandover,
    ])
    const actions = useActions({
        setHandover: uploadStore.actions.setHandover,
        startBatch: uploadStore.actions.startBatch,
        patchItem: uploadStore.actions.patchItem,
        dropItem: uploadStore.actions.dropItem,
        hideItem: uploadStore.actions.hideItem,
        finishBatch: uploadStore.actions.finishBatch,
        clearItems: uploadStore.actions.reset,
    })

    const abortRef = useRef<AbortController | null>(null)
    const isCancelledRef = useRef(false)
    const activeFileIdRef = useRef('')
    const cancelledIdsRef = useRef(new Set<string>())

    const isSettingsOpen = pendingFiles.length > 0

    // After a reload the live list is empty but the confirmed part of the batch came back from
    // sessionStorage — the review screen reads through this and recovers instead of going blank.
    const items = storeItems.length > 0 ? storeItems : restorableItems

    const displayedItems = storeItems.filter(item => displayedFileIds.includes(item.fileId))

    const updateItem = (fileId: string, patch: Partial<TUploadItem>) => {
        actions.patchItem({ fileId, patch })
    }

    const uploadOne = async (
        file: File,
        fileId: string,
        settings: TUploadSettings,
    ): Promise<TUploadedAudio | undefined> => {
        const controller = new AbortController()
        abortRef.current = controller
        activeFileIdRef.current = fileId

        let transfer: Awaited<ReturnType<typeof loadUploadAudioFile>>

        try {
            transfer = await loadUploadAudioFile()
        } catch (error) {
            if (controller.signal.aborted) {
                return undefined
            }

            Logger.error('Upload chunk error:', { error, payload: { fileName: file.name } })
            updateItem(fileId, { status: 'failed' })
            sendEvent({
                name: 'upload_audio_unsuccessful',
                payload: await getUploadFailureParams(error),
            })
            onError('upload-failed', file.name)

            return undefined
        }

        return transfer.uploadAudioFile({
            file,
            settings,
            signal: controller.signal,
            updateItem: patch => updateItem(fileId, patch),
            hideItem: () => actions.hideItem(fileId),
            onError,
        })
    }

    const startBatch = async (
        queued: TQueuedFile[],
        settings: TUploadSettings,
        { batchId, isAdopted }: TBatchOrigin,
    ) => {
        const totalSize = queued.reduce((sum, { file }) => sum + file.size, 0)
        const hasOwnReviewScreen = queued.length === 1

        isCancelledRef.current = false
        cancelledIdsRef.current = new Set()
        actions.startBatch({
            items: queued.map(({ file, fileId }) => ({
                fileId,
                fileName: file.name,
                categoryId: settings.categoryId,
                progress: 0,
                status: 'uploading' as const,
            })),
            displayedFileIds: hasOwnReviewScreen ? [] : queued.map(({ fileId }) => fileId),
        })

        // nextjs already announced this batch, and the document is already at its destination.
        if (!isAdopted) {
            sendEvent({
                name: 'upload_sound_batch_start',
                payload: {
                    categoryId: String(settings.categoryId),
                    batchId,
                    count: String(queued.length),
                    size: String(totalSize),
                },
            })

            onBatchStarted(queued.map(({ fileId }) => fileId))
        }

        onBatchFilesStarted(queued, { isAdopted })

        // A chain, not a loop: one upload at a time, and a cancel drops the rest of the queue.
        const uploaded = await queued.reduce<Promise<TUploadedAudio[]>>(
            async (chain, { file, fileId }) => {
                const done = await chain

                if (isCancelledRef.current || cancelledIdsRef.current.has(fileId)) {
                    return done
                }

                const audio = await uploadOne(file, fileId, settings)

                return audio ? [...done, audio] : done
            },
            Promise.resolve([]),
        )

        abortRef.current = null
        activeFileIdRef.current = ''
        actions.finishBatch()

        const [first] = uploaded

        if (first) {
            sendEvent({
                name: 'upload_sound_batch_finish',
                payload: {
                    batchId,
                    categoryId: String(settings.categoryId),
                    count: String(uploaded.length),
                    size: String(totalSize),
                    ids: uploaded.map(audio => audio.id),
                },
            })
        }

        onBatchFinished(uploaded, queued.length)
    }

    /** Not awaited: every failure path inside `startBatch` already reports itself. */
    const runBatch = (files: File[], settings: TUploadSettings) => {
        const queued = files.map(file => ({ file, fileId: generateId() }))

        startBatch(queued, settings, { batchId: generateId(), isAdopted: false }).catch(error => {
            Logger.error('Upload batch error:', { error })
        })
    }

    /** Ids come from nextjs, so `?fileId=` and the analytics batch still point at the same upload. */
    const adoptBatch = (queued: TQueuedFile[], settings: TUploadSettings, batchId: string) => {
        startBatch(queued, settings, { batchId, isAdopted: true }).catch(error => {
            Logger.error('Adopted upload batch error:', { error })
        })
    }

    const selectFiles = (files: File[]) => {
        // A second batch would take over the abort controller, leaving the first unstoppable.
        if (isUploading) {
            onError('upload-in-progress', '')

            return
        }

        const valid: File[] = []

        files.forEach(file => {
            const reason = validateAudioFile(file)

            if (reason) {
                onError(reason, file.name)

                return
            }

            valid.push(file)
        })

        if (valid.length === 0) {
            return
        }

        // audio.com asks for a category only when several files are picked at once.
        if (valid.length === 1) {
            runBatch(valid, DEFAULT_SETTINGS)

            return
        }

        setPendingFiles(valid)
    }

    const submitSettings = (settings: TUploadSettings) => {
        const files = pendingFiles

        setPendingFiles([])
        runBatch(files, settings)
    }

    const cancelSettings = () => {
        setPendingFiles([])
    }

    /** Nothing else clears `items`, and this hook outlives every screen that renders them. */
    const clear = useCallback(() => {
        actions.clearItems()
    }, [actions])

    /**
     * Drops one file from the batch, as audio-web's per-card abort does. Uploads run in sequence, so
     * a file that hasn't started yet is simply skipped when the chain reaches it; only the one in
     * flight has a transfer to abort.
     */
    const cancelItem = useCallback(
        (fileId: string) => {
            cancelledIdsRef.current.add(fileId)

            if (activeFileIdRef.current === fileId) {
                abortRef.current?.abort()
            }

            actions.dropItem(fileId)
        },
        [actions],
    )

    const adoptBatchRef = useRef(adoptBatch)
    const onErrorRef = useRef(onError)

    adoptBatchRef.current = adoptBatch
    onErrorRef.current = onError

    useEffect(() => {
        if (handover === 'none') {
            return
        }

        actions.setHandover('none')

        if (handover === 'failed') {
            onErrorRef.current('handover-failed', '')

            return
        }

        const pending = consumePendingHandover()

        if (pending) {
            adoptBatchRef.current(pending.entries, pending.settings, pending.batchId)
        }
    }, [handover, actions])

    // Stable so the provider's context value survives a progress tick.
    const cancel = useCallback(() => {
        isCancelledRef.current = true
        abortRef.current?.abort()
        abortRef.current = null
        actions.clearItems()
    }, [actions])

    return {
        items,
        displayedItems,
        isUploading,
        isSettingsOpen,
        pendingCount: pendingFiles.length,
        pendingFiles,
        selectFiles,
        adoptBatch,
        submitSettings,
        cancelSettings,
        clear,
        cancelItem,
        cancel,
    }
}
